import type { StatusColorName, StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import {
  mdiAlertCircleOutline,
  mdiCalendarCheckOutline,
  mdiCalendarClockOutline,
  mdiCloseCircleOutline,
  mdiEyeCheckOutline,
  mdiProgressClock,
} from "@mdi/js"
import type { ScheduleEventCommunicationPresentation } from "../../../api/kehrblatt/work-orders"
import { buildRichTooltip, tooltipField, tooltipText } from "../rich-tooltips"
import { buildAppointmentTimeTagPresentation } from "../appointment-time-tags"

export type ScheduleEventCommunicationPresentationInput = ScheduleEventCommunicationPresentation
export type ScheduleEventCommunicationEvidence = {
  kind: string | null
  channel: string | null
  at: string | null
}
export type ScheduleEventCommunicationTagPresentation = {
  definition: StatusDefinition<string>
  label: string
  detail: string
  evidence: ScheduleEventCommunicationEvidence | null
  nextAction: string | null
  tooltip: string
}

const styles: Record<string, { color: StatusColorName; icon: string; label: string }> = {
  RESERVED: { color: "orange", icon: mdiAlertCircleOutline, label: "Ankündigung fehlt" },
  ANNOUNCEMENT_RUNNING: { color: "blue", icon: mdiProgressClock, label: "Ankündigung läuft" },
  ANNOUNCEMENT_SENT: {
    color: "blue",
    icon: mdiProgressClock,
    label: "Nachricht versendet · Bestätigung offen",
  },
  ANNOUNCEMENT_FAILED: {
    color: "rose",
    icon: mdiAlertCircleOutline,
    label: "Ankündigung fehlgeschlagen",
  },
  ANNOUNCEMENT_UNSECURED: {
    color: "orange",
    icon: mdiAlertCircleOutline,
    label: "Versendet · Nachweis offen",
  },
  ANNOUNCEMENT_SKIPPED: {
    color: "neutral",
    icon: mdiCloseCircleOutline,
    label: "Ankündigung ausgelassen",
  },
  ANNOUNCEMENT_UNKNOWN: {
    color: "orange",
    icon: mdiAlertCircleOutline,
    label: "Ankündigung prüfen",
  },
  LETTER_QUEUED: { color: "blue", icon: mdiProgressClock, label: "Briefversand beauftragt" },
  LETTER_PENDING: { color: "orange", icon: mdiCalendarClockOutline, label: "Brief zustellen" },
  WHATSAPP_READ: {
    color: "forest",
    icon: mdiEyeCheckOutline,
    label: "Angekündigt · WhatsApp gelesen",
  },
  LETTER_DELIVERED: {
    color: "forest",
    icon: mdiEyeCheckOutline,
    label: "Angekündigt · Brief zugestellt",
  },
  LETTER_SENT: {
    color: "forest",
    icon: mdiEyeCheckOutline,
    label: "Angekündigt · Brief versendet",
  },
  SECURELY_ANNOUNCED: { color: "forest", icon: mdiEyeCheckOutline, label: "Sicher angekündigt" },
  CONFIRMED: { color: "emerald", icon: mdiCalendarCheckOutline, label: "Bestätigt" },
  COMPLETED: { color: "neutral", icon: mdiCalendarCheckOutline, label: "Durchgeführt" },
  CANCELED: { color: "neutral", icon: mdiCloseCircleOutline, label: "Abgesagt" },
  NOSHOW: { color: "rose", icon: mdiCalendarClockOutline, label: "Nicht stattgefunden" },
  UNKNOWN: {
    color: "neutral",
    icon: mdiCalendarClockOutline,
    label: "Ankündigungsstatus unbekannt",
  },
}
const dateLabel = (value: string) => {
  const date = new Date(value)

  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleString("de-DE", {
        timeZone: "Europe/Berlin",
        dateStyle: "short",
        timeStyle: "short",
      })
}
const channelLabels: Record<string, string> = {
  EMAIL: "E-Mail",
  WHATSAPP: "WhatsApp",
  LETTER: "Brief",
  MOBILE_MESSAGE: "Nachricht",
  SMS: "SMS",
  PHONE: "Telefon",
  VOICE: "Telefon",
  VOICE_BOT: "Telefon",
}

export const buildScheduleEventCommunicationTagPresentation = ({
  status,
  presentation = null,
  event = null,
  now = new Date(),
}: {
  status: ScheduleEventStatus | string
  presentation?: ScheduleEventCommunicationPresentationInput | null
  event?: ScheduleEvent | null
  now?: Date
}): ScheduleEventCommunicationTagPresentation => {
  const source = presentation ?? event?.communication_presentation
  // An old ANNOUNCED flag alone may originate from a PDF download, not delivery.
  const state = ["CONFIRMED", "COMPLETED", "CANCELED", "NOSHOW"].includes(status)
    ? status
    : (source?.state ?? "UNKNOWN")
  const base = styles[state] ?? styles.UNKNOWN
  let label = base.label
  let color = base.color

  if (state === "WHATSAPP_READ" && source?.next_action === "automatic") {
    label = "WhatsApp gelesen · Brief bereits beauftragt"
  }

  const due = source?.next_action_at ? new Date(source.next_action_at) : null

  if (state === "LETTER_PENDING" && source?.next_action === "automatic") {
    color = "blue"
    label =
      due && due.getTime() > now.getTime()
        ? `Automatischer Briefversand ab ${due.toLocaleDateString("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit" })}`
        : "Automatischer Briefversand vorgesehen"
  } else if (state === "LETTER_PENDING" && due && due.getTime() > now.getTime()) {
    color = "blue"
    label = `Brief zustellen ab ${due.toLocaleDateString("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit" })}`
  }

  const evidence =
    source?.evidence_kind || source?.evidence_at
      ? {
          kind: source.evidence_kind ?? null,
          channel: source.evidence_channel ?? null,
          at: source.evidence_at ?? null,
        }
      : null
  const nextAction = ["CONFIRMED", "COMPLETED", "CANCELED", "NOSHOW"].includes(state)
    ? null
    : (source?.next_action_label ?? null)
  const detail =
    (source?.state === state ? source.detail : null) ??
    (state === "CONFIRMED"
      ? "Der Termin wurde ausdrücklich bestätigt."
      : state === "UNKNOWN"
        ? "Die Ankündigungsdaten sind noch nicht verfügbar."
        : "")
  const evidenceLabel =
    evidence?.kind === "whatsapp_read"
      ? "Lesebestätigung erhalten am"
      : evidence?.kind === "letter_delivered"
        ? "Zugestellt am"
        : "Versendet am"
  const confirmedBy =
    event?.appointment_contact?.confirmed_by_label ??
    event?.confirmed_by_participant?.display_name ??
    event?.lifecycle_summary?.confirmed_by_label
  const confirmedAt =
    event?.appointment_contact?.confirmed_at ?? event?.lifecycle_summary?.confirmed_at
  const time = buildAppointmentTimeTagPresentation(event)
  const tooltip = buildRichTooltip([
    time.usesCustomerWindow ? tooltipField("Kalenderzeit", time.scheduledPeriodLabel) : null,
    time.tooltipDescription ? tooltipText(time.tooltipDescription) : null,
    state === "CONFIRMED" ? tooltipField("Bestätigt durch", confirmedBy) : null,
    state === "CONFIRMED" && confirmedAt
      ? tooltipField("Bestätigt am", dateLabel(confirmedAt))
      : null,
    evidence?.at ? tooltipField(evidenceLabel, dateLabel(evidence.at)) : null,
    state === "CONFIRMED" && source?.announcement_label
      ? tooltipField("Ankündigung", source.announcement_label)
      : null,
    state === "ANNOUNCEMENT_RUNNING" && source?.evidence_channel
      ? tooltipField(
          "Kontaktweg",
          channelLabels[source.evidence_channel] ?? source.evidence_channel,
        )
      : null,
    tooltipText(detail),
    nextAction
      ? tooltipField("Nächster Schritt", nextAction)
      : source?.next_action === "none" && !["COMPLETED", "CANCELED", "NOSHOW"].includes(state)
        ? tooltipText("Keine weitere Ankündigung nötig.")
        : null,
  ])

  return {
    definition: { value: state, label, color, icon: base.icon, description: detail },
    label,
    detail,
    evidence,
    nextAction,
    tooltip,
  }
}
